import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import {
  Archive,
  Calendar,
  CalendarPlus,
  Clapperboard,
  ClipboardList,
  CreditCard,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  ListChecks,
  Menu,
  Settings,
  UserCircle,
  Users,
  Video,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  getActiveLscNavKey,
  MOBILE_NAV_LABEL_IMPORT_SCHEDULE,
  type LeagueNavItem,
  type LscLeagueNavKey,
} from '../leagueNav';
import { lscPath } from '../routing';
import { ReportIssueTrigger } from './support/ReportIssueTrigger';

const LEAGUE_NAV_ICONS: Record<LscLeagueNavKey, LucideIcon> = {
  'command-center': LayoutDashboard,
  home: LayoutDashboard,
  setup: ListChecks,
  members: Users,
  import: CalendarPlus,
  'cfb-setup': GraduationCap,
  'cfb-schedule': Calendar,
  matches: Calendar,
  'needs-attention': ClipboardList,
  vods: Video,
  review: ClipboardList,
  archive: Archive,
  rtts: Clapperboard,
  settings: Settings,
};

function drawerLinkClass(isActive: boolean): string {
  return [
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
    isActive
      ? 'bg-primary-900/50 text-primary-200 border border-primary-700'
      : 'text-secondary-300 hover:bg-secondary-800 hover:text-white',
  ].join(' ');
}

const MOBILE_DRAWER_MANAGE_KEYS = new Set<LscLeagueNavKey>(['setup', 'members', 'import', 'cfb-setup']);

function mobileDrawerLabel(item: LeagueNavItem): string {
  if (item.navKey === 'import') {
    return MOBILE_NAV_LABEL_IMPORT_SCHEDULE;
  }
  return item.label;
}

interface LscMobileNavDrawerProps {
  leagueId: string;
  leagueName: string;
  leagueNav: LeagueNavItem[];
  userDisplayName?: string | null;
  onSignOut: () => void;
}

const LscMobileNavDrawer: React.FC<LscMobileNavDrawerProps> = ({
  leagueId,
  leagueName,
  leagueNav,
  userDisplayName,
  onSignOut,
}) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const activeKey = getActiveLscNavKey(location.pathname, leagueId);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);

  const handleSignOut = () => {
    close();
    onSignOut();
  };

  return (
    <>
      <button
        type="button"
        className="md:hidden inline-flex items-center justify-center rounded-lg border border-secondary-600 bg-secondary-800 p-2 text-secondary-200 hover:bg-secondary-700 hover:text-white"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls="lsc-mobile-nav-drawer"
        data-testid="lsc-mobile-nav-menu-button"
      >
        <Menu className="h-5 w-5" aria-hidden />
        <span className="sr-only">Open league navigation</span>
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-50 bg-black/60 md:hidden"
            aria-label="Close league navigation"
            onClick={close}
            data-testid="lsc-mobile-nav-drawer-backdrop"
          />
          <aside
            id="lsc-mobile-nav-drawer"
            className="fixed inset-y-0 left-0 z-50 flex w-[min(100%,20rem)] flex-col border-r border-secondary-700 bg-secondary-900 md:hidden"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
            role="dialog"
            aria-modal="true"
            aria-label="League navigation"
            data-testid="lsc-mobile-nav-drawer"
          >
            <div className="flex items-center justify-between border-b border-secondary-700 px-4 py-3">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-secondary-500">League</p>
                <p className="truncate text-sm font-semibold text-white">{leagueName}</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-secondary-400 hover:bg-secondary-800 hover:text-white"
                onClick={close}
                aria-label="Close menu"
                data-testid="lsc-mobile-nav-drawer-close"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <nav
              className="flex-1 overflow-y-auto p-4 space-y-1"
              aria-label="League navigation"
              data-testid="lsc-mobile-league-nav"
            >
              {leagueNav.map((item, index) => {
                const Icon = LEAGUE_NAV_ICONS[item.navKey];
                const isActive = activeKey === item.navKey;
                const showManageHeader =
                  MOBILE_DRAWER_MANAGE_KEYS.has(item.navKey) &&
                  !MOBILE_DRAWER_MANAGE_KEYS.has(leagueNav[index - 1]?.navKey ?? ('' as LscLeagueNavKey));
                return (
                  <React.Fragment key={item.to}>
                    {showManageHeader ? (
                      <p
                        className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500"
                        data-testid="lsc-mobile-drawer-manage-header"
                      >
                        Manage league
                      </p>
                    ) : null}
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={drawerLinkClass(isActive)}
                      onClick={close}
                      data-testid={`lsc-mobile-drawer-nav-${item.navKey}`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      {mobileDrawerLabel(item)}
                    </NavLink>
                  </React.Fragment>
                );
              })}
              <span className="sr-only" data-testid="lsc-mobile-drawer-nav-labels">
                {leagueNav.map((item) => mobileDrawerLabel(item)).join(',')}
              </span>
            </nav>

            <nav
              className="border-t border-secondary-700 p-4 space-y-1"
              aria-label="Account navigation"
              data-testid="lsc-mobile-account-nav"
            >
              <NavLink to={lscPath('/leagues')} className={drawerLinkClass(false)} onClick={close}>
                <LayoutDashboard className="h-4 w-4 shrink-0" aria-hidden />
                Leagues
              </NavLink>
              <NavLink
                to={lscPath('/settings')}
                className={drawerLinkClass(false)}
                onClick={close}
                data-testid="lsc-mobile-drawer-account-settings"
              >
                <UserCircle className="h-4 w-4 shrink-0" aria-hidden />
                Account settings
              </NavLink>
              <NavLink
                to={lscPath('/pricing')}
                className={drawerLinkClass(false)}
                onClick={close}
                data-testid="lsc-mobile-drawer-pricing"
              >
                <CreditCard className="h-4 w-4 shrink-0" aria-hidden />
                Pricing
              </NavLink>
              <NavLink
                to={lscPath('/billing')}
                className={drawerLinkClass(false)}
                onClick={close}
                data-testid="lsc-mobile-drawer-billing"
              >
                <CreditCard className="h-4 w-4 shrink-0" aria-hidden />
                Billing
              </NavLink>
              <ReportIssueTrigger
                variant="nav"
                testId="lsc-mobile-drawer-report-issue"
                prefill={{ surface: 'lsc_mobile_drawer' }}
              />
              {userDisplayName && (
                <p className="px-3 pt-2 text-xs text-secondary-500 truncate">{userDisplayName}</p>
              )}
              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-secondary-400 hover:bg-secondary-800 hover:text-red-300"
                data-testid="lsc-mobile-drawer-sign-out"
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                Sign out
              </button>
            </nav>
          </aside>
        </>
      )}
    </>
  );
};

export default LscMobileNavDrawer;
